'use client';
import * as React from 'react';
import * as RPopover from '@radix-ui/react-popover';
import * as RMenu from '@radix-ui/react-dropdown-menu';
import * as RTooltip from '@radix-ui/react-tooltip';
import { Check } from 'lucide-react';
import { cn } from '../cn';

// ---- Popover ---------------------------------------------------------------
export const Popover = RPopover.Root;
export const PopoverTrigger = RPopover.Trigger;
export const PopoverClose = RPopover.Close;
export function PopoverContent({ className, sideOffset = 6, ...props }: React.ComponentPropsWithoutRef<typeof RPopover.Content>) {
  return (
    <RPopover.Portal>
      <RPopover.Content sideOffset={sideOffset} className={cn('ui-rise z-[60] w-72 rounded-[var(--radius-lg)] bg-surface p-4 shadow-[var(--shadow-lg)] outline-none', className)} {...props} />
    </RPopover.Portal>
  );
}

// ---- Dropdown menu ---------------------------------------------------------
export const DropdownMenu = RMenu.Root;
export const DropdownMenuTrigger = RMenu.Trigger;
export const DropdownMenuGroup = RMenu.Group;
export const DropdownMenuSub = RMenu.Sub;
export const DropdownMenuRadioGroup = RMenu.RadioGroup;
export function DropdownMenuContent({ className, sideOffset = 6, ...props }: React.ComponentPropsWithoutRef<typeof RMenu.Content>) {
  return (
    <RMenu.Portal>
      <RMenu.Content sideOffset={sideOffset} className={cn('ui-rise z-[60] min-w-[200px] rounded-[var(--radius)] bg-surface p-1 shadow-[var(--shadow-lg)] outline-none', className)} {...props} />
    </RMenu.Portal>
  );
}
export function DropdownMenuItem({ className, destructive, icon, ...props }: React.ComponentPropsWithoutRef<typeof RMenu.Item> & { destructive?: boolean; icon?: React.ReactNode }) {
  return (
    <RMenu.Item className={cn('relative flex cursor-default select-none items-center gap-2 rounded-[var(--radius-sm)] px-2.5 py-1.5 text-[14px] outline-none data-[highlighted]:bg-accent data-[highlighted]:text-white data-[disabled]:opacity-40', destructive ? 'text-red data-[highlighted]:bg-red' : 'text-text', className)} {...props}>
      {props.asChild ? props.children : <>{icon ? <span className="[&>svg]:size-4 inline-flex opacity-80">{icon}</span> : null}{props.children}</>}
    </RMenu.Item>
  );
}
export function DropdownMenuCheckboxItem({ className, children, ...props }: React.ComponentPropsWithoutRef<typeof RMenu.CheckboxItem>) {
  return (
    <RMenu.CheckboxItem className={cn('relative flex cursor-default select-none items-center rounded-[var(--radius-sm)] py-1.5 pl-8 pr-2.5 text-[14px] text-text outline-none data-[highlighted]:bg-accent data-[highlighted]:text-white', className)} {...props}>
      <RMenu.ItemIndicator className="absolute left-2.5 inline-flex">
        <Check className="size-4" />
      </RMenu.ItemIndicator>
      {children}
    </RMenu.CheckboxItem>
  );
}
export function DropdownMenuLabel({ className, ...props }: React.ComponentPropsWithoutRef<typeof RMenu.Label>) {
  return <RMenu.Label className={cn('px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-text-3', className)} {...props} />;
}
export function DropdownMenuSeparator({ className, ...props }: React.ComponentPropsWithoutRef<typeof RMenu.Separator>) {
  return <RMenu.Separator className={cn('my-1 h-px bg-separator', className)} {...props} />;
}

// ---- Tooltip ---------------------------------------------------------------
export const TooltipProvider = RTooltip.Provider;
export function Tooltip({ content, children, side = 'top', delayDuration = 300 }: { content: React.ReactNode; children: React.ReactNode; side?: 'top' | 'bottom' | 'left' | 'right'; delayDuration?: number }) {
  return (
    <RTooltip.Root delayDuration={delayDuration}>
      <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
      <RTooltip.Portal>
        <RTooltip.Content side={side} sideOffset={6} className="ui-rise z-[70] max-w-[280px] rounded-[var(--radius-sm)] bg-[var(--text)] px-2.5 py-1.5 text-[12px] text-[var(--bg)] shadow-[var(--shadow-md)]">
          {content}
        </RTooltip.Content>
      </RTooltip.Portal>
    </RTooltip.Root>
  );
}
